/*
 * L'HISTORIQUE DU SHELL : ce que chaque ETAPE du navigateur retient, et les
 * regles qui l'ecrivent. Module PUR, testable sans React ni navigateur.
 *
 * DEUX FAITS RANGES DANS L'ETAT DE L'ETAPE (`history.state`), JAMAIS DANS
 * L'ADRESSE (decision du fondateur du 04/11/2026, `docs/socle/fil-ariane-historique.md`) :
 *
 *  - `tkOrigine` : D'OU L'ON VIENT quand on ouvre une fiche (la puce « Revenir a
 *    <fiche> »). Le navigateur garde l'etat de chaque etape : l'origine survit
 *    donc au F5, a Precedent et Suivant et aux sauts entre apps, ce que ni une
 *    ref de l'ecran ni une ref du shell ne savaient faire. Un lien copie n'en
 *    porte pas : « je viens de quelque part » ne se partage pas.
 *  - `tkEntree` : PAR OU L'ON EST ENTRE dans la zone courante (une app, le
 *    bureau, un hub). C'est lui qui dit a l'icone d'app du rail jusqu'ou
 *    RECULER dans l'historique pour revenir « d'ou l'on vient », au lieu
 *    d'empiler une etape de plus.
 *
 * LE PIEGE DU ROUTEUR QUI IMPOSE LE FILET. Sans `state`, une navigation de
 * TanStack Router repart d'un etat VIDE : un simple changement de vue (un
 * remplacement) effacait l'origine de la fiche. D'ou `decorerHistorique`, qui
 * reporte les deux clefs au bas niveau, quel que soit le site de navigation
 * (dock, raccourci, Lia, saut entre apps), et qui est l'unique endroit ou
 * l'entree dans une zone est tamponnee.
 */
import type { OrigineDEtape, OrigineDeFiche, ScreenSearch } from '@taki/ui';

/** La clef de l'origine dans `history.state`. */
export const CLEF_ORIGINE = 'tkOrigine';
/** La clef de l'entree dans la zone dans `history.state`. */
export const CLEF_ENTREE = 'tkEntree';
/** La clef du rang de l'etape, posee par l'historique de TanStack. */
const CLEF_RANG = '__TSR_index';

/** Combien de `parent` une origine emporte au plus : au-dela, le plus ancien est coupe. */
export const PROFONDEUR_MAX = 5;

/** L'entree dans une zone : l'app quittee (`null` = le bureau) et le rang de son etape. */
export interface EntreeDeZone {
  app: string | null;
  rang: number;
}

const estObjet = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const chaine = (v: unknown): string | null =>
  typeof v === 'string' && v !== '' ? v : null;

const nombre = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/**
 * Lit une origine ecrite, sans rien croire : un etat d'historique vient du
 * navigateur (donc d'une session precedente, d'une autre version du code) et
 * peut avoir n'importe quelle forme. `null` partout ou la forme ne tient pas,
 * jamais d'exception. La profondeur lue est bornee elle aussi : un etat forge
 * ne doit pas faire boucler la lecture.
 */
function lireOrigineBrute(brut: unknown, org: string | null, restant: number): OrigineDEtape | null {
  if (!estObjet(brut)) return null;
  const porte = chaine(brut['porte']);
  const id = chaine(brut['id']);
  const titre = chaine(brut['titre']);
  const app = chaine(brut['app']);
  if (porte === null || id === null || titre === null || app === null) return null;
  const adresseBrute = brut['adresse'];
  if (!estObjet(adresseBrute)) return null;
  const adresse: ScreenSearch = {};
  for (const [clef, valeur] of Object.entries(adresseBrute)) {
    if (typeof valeur !== 'string') return null;
    adresse[clef] = valeur;
  }
  const rangBrut = brut['rang'];
  const rang = rangBrut === null || rangBrut === undefined ? null : nombre(rangBrut);
  if (rang === null && rangBrut !== null && rangBrut !== undefined) return null;
  const orgBrute = brut['org'];
  if (orgBrute !== null && orgBrute !== undefined && typeof orgBrute !== 'string') return null;
  const orgDeLOrigine = typeof orgBrute === 'string' ? orgBrute : null;
  // UNE AUTRE ORG NE RELIT JAMAIS CETTE ORIGINE : le titre d'une fiche d'un
  // client n'a rien a faire sur l'ecran d'un autre.
  if (orgDeLOrigine !== org) return null;
  const parentBrut = brut['parent'];
  let parent: OrigineDEtape | null = null;
  if (parentBrut !== null && parentBrut !== undefined) {
    if (restant <= 0) return null;
    parent = lireOrigineBrute(parentBrut, org, restant - 1);
    if (parent === null) return null;
  }
  return { porte, id, titre, adresse, app, rang, org: orgDeLOrigine, parent };
}

/**
 * L'ORIGINE QUE PORTE `state`, ou `null` : pas d'origine, forme inattendue, ou
 * origine ecrite pour une autre org que `org`.
 */
export function lireOrigine(state: unknown, org: string | null): OrigineDEtape | null {
  if (!estObjet(state)) return null;
  return lireOrigineBrute(state[CLEF_ORIGINE], org, PROFONDEUR_MAX);
}

/** Le rang de l'etape (`__TSR_index`), ou `null` quand l'historique n'en pose pas. */
export function rangDeLEtat(state: unknown): number | null {
  if (!estObjet(state)) return null;
  return nombre(state[CLEF_RANG]);
}

/** Coupe la chaine des parents a `restant` niveaux. */
function couper(origine: OrigineDEtape, restant: number): OrigineDEtape {
  if (origine.parent === null || restant <= 0) return { ...origine, parent: null };
  return { ...origine, parent: couper(origine.parent, restant - 1) };
}

/**
 * COMPLETE CE QUE L'ECRAN SAIT (`depuis`) avec ce que seul le shell sait : l'app
 * de la fiche quittee, le rang de son etape et l'org.
 *
 * LE PARENT N'EST GARDE QUE SI L'ORIGINE COURANTE PARLE DE LA FICHE QUE L'ON
 * QUITTE. L'etape courante porte l'origine de SA fiche (« je viens de X ») : si
 * l'on quitte cette fiche pour une autre, X devient le parent, le retour pourra
 * le rendre. Mais une origine qui parle d'une autre fiche (l'ecran a ouvert
 * autre chose entre-temps) ne dit rien de celle-ci : la recopier ferait
 * mentir le retour.
 */
export function completerOrigine(
  depuis: OrigineDeFiche,
  contexte: {
    app: string;
    rang: number | null;
    org: string | null;
    origineCourante: OrigineDEtape | null;
    paramsCourants: ScreenSearch;
  },
): OrigineDEtape {
  const { origineCourante, paramsCourants } = contexte;
  const parle =
    origineCourante !== null &&
    origineCourante.id === paramsCourants['id'] &&
    origineCourante.porte === paramsCourants['s'];
  return {
    porte: depuis.porte,
    id: depuis.id,
    titre: depuis.titre,
    adresse: depuis.adresse,
    app: contexte.app,
    rang: contexte.rang,
    org: contexte.org,
    parent: parle && origineCourante !== null ? couper(origineCourante, PROFONDEUR_MAX - 1) : null,
  };
}

/** Le pathname d'une adresse d'historique (`/app/x?s=y#z` -> `/app/x`). */
export function pathnameDe(href: string): string {
  const fin = href.search(/[?#]/);
  return fin === -1 ? href : href.slice(0, fin);
}

/**
 * LA ZONE D'UNE ROUTE : ce qui fait qu'on « entre » quelque part. `/app/<id>` est
 * la zone de l'app `<id>`, le bureau (`/` et ses sections `/account`, `/store`)
 * est la sienne, et toute autre route (hub Parametres, Studio, journal) en est
 * une troisieme, commune.
 */
export type ZoneDeRoute =
  | { genre: 'app'; id: string }
  | { genre: 'bureau' }
  | { genre: 'autre' };

export function zoneDe(pathname: string): ZoneDeRoute {
  const app = /^\/app\/([^/]+)/.exec(pathname);
  if (app !== null && app[1] !== undefined) {
    let id = app[1];
    try {
      id = decodeURIComponent(id);
    } catch {
      /* Un pourcentage mal forme reste tel quel : la zone n'en vaut pas moins. */
    }
    return { genre: 'app', id };
  }
  if (pathname === '/' || pathname === '' || pathname === '/account' || pathname === '/store') {
    return { genre: 'bureau' };
  }
  return { genre: 'autre' };
}

const memeZone = (a: ZoneDeRoute, b: ZoneDeRoute): boolean =>
  a.genre === b.genre && (a.genre !== 'app' || (b.genre === 'app' && a.id === b.id));

/** L'entree que porte `state`, ou `null`. */
export function entreeDeLEtat(state: unknown): EntreeDeZone | null {
  if (!estObjet(state)) return null;
  const brute = state[CLEF_ENTREE];
  if (!estObjet(brute)) return null;
  const rang = nombre(brute['rang']);
  if (rang === null) return null;
  const app = brute['app'];
  if (app === null) return { app: null, rang };
  const nom = chaine(app);
  return nom === null ? null : { app: nom, rang };
}

/** Ce que l'historique sait de l'etape courante. */
export interface EtapeCourante {
  pathname: string;
  state: unknown;
}

function copie(state: unknown): Record<string, unknown> {
  return estObjet(state) ? { ...state } : {};
}

/**
 * L'ETAT A DONNER A UN PUSH. `tkOrigine` n'est JAMAIS reporte : une origine
 * parle d'une fiche, et la nouvelle etape en est une autre. Il n'y est que si
 * l'appelant l'a donne.
 *
 * `tkEntree`, lui, dit par ou l'on est entre dans la zone ou l'on pousse : s'il
 * est donne il gagne ; dans la meme zone on reporte celui de l'etape courante
 * (on y est toujours entre au meme endroit) ; en changeant de zone, l'entree est
 * l'etape qu'on QUITTE, app ou bureau (un hub ou le Studio ne sont pas des
 * endroits ou l'on revient).
 */
export function etatPousse(
  courant: EtapeCourante,
  ciblePathname: string,
  stateDonne: unknown,
): Record<string, unknown> {
  const etat = copie(stateDonne);
  if (etat[CLEF_ENTREE] === null) delete etat[CLEF_ENTREE];
  if (etat[CLEF_ORIGINE] === null) delete etat[CLEF_ORIGINE];
  if (etat[CLEF_ENTREE] !== undefined) return etat;
  const zoneCourante = zoneDe(courant.pathname);
  const zoneCible = zoneDe(ciblePathname);
  if (memeZone(zoneCourante, zoneCible)) {
    const entree = entreeDeLEtat(courant.state);
    if (entree !== null) etat[CLEF_ENTREE] = entree;
    return etat;
  }
  const rang = rangDeLEtat(courant.state);
  if (rang === null || zoneCible.genre === 'bureau') return etat;
  if (zoneCourante.genre === 'app') etat[CLEF_ENTREE] = { app: zoneCourante.id, rang };
  else if (zoneCourante.genre === 'bureau') etat[CLEF_ENTREE] = { app: null, rang };
  return etat;
}

/**
 * L'ETAT A DONNER A UN REMPLACEMENT : c'est le filet du piege du routeur. Sur la
 * meme route, toute clef ABSENTE de l'etat donne est reportee de l'etape
 * courante (changer de vue ne perd pas l'origine) ; une valeur `null` donnee
 * l'EFFACE. Sur une autre route, l'etape change de nature : seules les clefs
 * donnees comptent.
 */
export function etatRemplace(
  courant: EtapeCourante,
  ciblePathname: string,
  stateDonne: unknown,
): Record<string, unknown> {
  const etat = copie(stateDonne);
  const actuel = copie(courant.state);
  const memeRoute = pathnameDe(courant.pathname) === pathnameDe(ciblePathname);
  for (const clef of [CLEF_ORIGINE, CLEF_ENTREE]) {
    if (etat[clef] === undefined && memeRoute && actuel[clef] !== undefined) etat[clef] = actuel[clef];
    if (etat[clef] === null) delete etat[clef];
  }
  return etat;
}

/**
 * LE `state` D'UNE NAVIGATION DU ROUTEUR, en fonction de l'etat precedent. Le
 * routeur type son etat par une interface vide (`HistoryState`) : nos clefs n'y
 * sont pas declarees, et ce package ne peut pas l'augmenter sans dependre de
 * `@tanstack/history`. Cette fonction ne fait que mettre le type a la
 * frontiere, une seule fois, au lieu d'un cast a chaque site de navigation.
 */
export function majEtat(
  fn: (precedent: Record<string, unknown>) => Record<string, unknown>,
): (precedent: object) => object {
  return (precedent) => fn(precedent as Record<string, unknown>);
}

/** Ce que `decorerHistorique` exige de l'historique : l'etape courante, push et replace. */
export interface HistoriqueDecorable {
  location: { pathname: string; state: unknown };
  push(path: string, state?: unknown, navigateOpts?: unknown): void;
  replace(path: string, state?: unknown, navigateOpts?: unknown): void;
}

const decores = new WeakSet<object>();

/**
 * BRANCHE LES DEUX REGLES SUR L'HISTORIQUE, EN PLACE. On remplace `push` et
 * `replace` sur l'objet lui-meme et on ne le recopie pas : `location` est un
 * accesseur que le routeur lit sur CET objet. Idempotent (un rechargement a
 * chaud ne decore pas deux fois).
 */
export function decorerHistorique<H extends HistoriqueDecorable>(historique: H): H {
  if (decores.has(historique)) return historique;
  decores.add(historique);
  const push = historique.push.bind(historique);
  const replace = historique.replace.bind(historique);
  historique.push = (path, state, navigateOpts) => {
    push(path, etatPousse(historique.location, pathnameDe(path), state), navigateOpts);
  };
  historique.replace = (path, state, navigateOpts) => {
    replace(path, etatRemplace(historique.location, pathnameDe(path), state), navigateOpts);
  };
  return historique;
}
